import _ from 'lodash';
import type {
    ChildAccidentMissingInformationDTO,
    ContentMissingInformationDTO,
    SingleTripMissingInformationDTO,
    PetMissingInformationDTO,
    MissingInformationDTO,
    AccidentYoungAndOtherPersonMissingInformationDTO,
    CarMissingInformationDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/missingInformation';
import {
    isMissingChildAccidentData,
    isMissingPetChipId,
    isMissingPRCCohousing,
    isMissingPRCCoinsuredPartner,
    isMissingSingleTripData,
    isMissingAdultYoungAccidentOtherPersonData
} from '../pages/CheckoutPage/configs/CheckMissingCoverableConfig';
import type {
    AdultAccidentDTO,
    ChildAccidentDTO,
    YoungAccidentDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/accident';
import type { LocalDateDTO } from 'gw-edge-client/dist/types/dto/pc/edge/time/';
import type { SingleTripTravelDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/stt';
import type { SingleTripCancellationDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/stc';
import type {
    CatDTO,
    DogDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/pet';
import type { AllCoverablesDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type { PrivateContentDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/content';
import type { CarDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/mvp';
import { replaceAllCharactersButNumbers } from './stringUtil';

const getCpr = (dob: LocalDateDTO | undefined, cpr: string): string => {
    let cprFromPC = '';
    if (cpr !== '') {
        cprFromPC = cpr;
    } else if (dob) {
        const day = dob.day;
        const month = dob.month;
        const year = dob.year;
        if (day && month && year) {
            const yearCharacters = year?.toString().split('');
            const lastTwoDigitsOfYear = `${yearCharacters[2]}${yearCharacters[3]}`;
            cprFromPC = `${day}${month}${lastTwoDigitsOfYear}`;
        }
    }
    return cprFromPC;
};

const initialSingleTripMissingDTO = (
    missingSingleTripCoverable: SingleTripTravelDTO | SingleTripCancellationDTO | undefined
): SingleTripMissingInformationDTO[] | undefined => {
    if (!missingSingleTripCoverable) return undefined;
    const numberOfPersonOutsideHousehold = getSingleTripNumberMissingPersonDifference(
        missingSingleTripCoverable
    );
    if (!numberOfPersonOutsideHousehold || numberOfPersonOutsideHousehold <= 0) return undefined;
    return Array.from({ length: numberOfPersonOutsideHousehold }, (_, fixedId) => ({
        cpr: '',
        address: undefined,
        fixedId: `mock-${(fixedId + 1).toString()}`,
        surname: '',
        name: ''
    }));
};

const mapChildAccidentCoverableToMissingInformationDTO = (
    coverables: ChildAccidentDTO[]
): ChildAccidentMissingInformationDTO[] => {
    return (
        coverables
            ?.filter((child) => isMissingChildAccidentData(child))
            .map((child) => {
                const missingChild: ChildAccidentMissingInformationDTO = {
                    fullName: child.fullName,
                    firstName: child.firstName,
                    lastName: child.lastName,
                    cpr: replaceAllCharactersButNumbers(child.cprNumber ?? ''),
                    fixedId: child.fixedId,
                    isDummyContact: child.isDummyContact,
                    address: child.address
                };
                return missingChild;
            }) ?? []
    );
};

const mapAccidentOtherPersonCoverableToMissingInformationDTO = (
    coverablesAdult: AdultAccidentDTO[],
    coverablesYoung: YoungAccidentDTO[]
): AccidentYoungAndOtherPersonMissingInformationDTO[] => {
    const combinedAdultAndYoung = coverablesAdult.concat(coverablesYoung);
    return (
        combinedAdultAndYoung
            ?.filter((otherPerson) => isMissingAdultYoungAccidentOtherPersonData(otherPerson))
            .map((otherPerson) => {
                const otherPersonMissingInfo: AccidentYoungAndOtherPersonMissingInformationDTO = {
                    flavourName: otherPerson.flavourName ?? '',
                    fullName: otherPerson.fullName,
                    firstName: otherPerson.firstName,
                    lastName: otherPerson.lastName,
                    cpr: replaceAllCharactersButNumbers(otherPerson.cprNumber ?? ''),
                    fixedId: otherPerson.fixedId,
                    address: otherPerson.address,
                    isDummyContact: otherPerson.isDummyContact,
                    phoneNumber: otherPerson.phoneNumber,
                    email: otherPerson.email,
                    dawaId: otherPerson.dawaId
                };
                return otherPersonMissingInfo;
            }) ?? []
    );
};

const mapCarCoverableToMissingInformationDTO = (
    coverables: CarDTO[]
): CarMissingInformationDTO[] => {
    return (
        coverables
            ?.filter((item) => !item.odometerReading)
            .map((carDTO) => {
                const carMissingDTO: CarMissingInformationDTO = {
                    registrationNumber: carDTO.registrationNumber ?? '',
                    fixedId: carDTO.fixedId,
                    odometerReading: carDTO.odometerReading,
                    hasOdometer: carDTO.odometerReading ? true : undefined
                };
                return carMissingDTO;
            }) ?? []
    );
};

const reformatCprWithoutHyphen = (cpr: string): string => {
    return cpr.replace(/[\s-]/g, '');
};

const reformatAndGetCprInformation = (
    cpr: string
): { dateOfBirth?: string; identificationNumber?: string } => {
    cpr = reformatCprWithoutHyphen(cpr);
    const cprPattern = /^(\d{6})(\d{0,4})$/;
    return cpr.match(cprPattern)
        ? {
              dateOfBirth: cpr.replace(cprPattern, '$1'),
              identificationNumber: cpr.replace(cprPattern, '$2')
          }
        : {};
};

const createMissingInformationDTOByQuantity = (
    numberOfProducts: number | undefined
): ContentMissingInformationDTO[] => {
    if (!numberOfProducts || numberOfProducts === 0) return [];
    return Array.from({ length: numberOfProducts }, (_, fixedId) => ({
        cpr: '',
        fixedId: `mock-${(fixedId + 1).toString()}`,
        surname: '',
        name: ''
    }));
};

const getSingleTripNumberMissingPersonDifference = (
    missingSttCoverable: SingleTripTravelDTO | SingleTripCancellationDTO
): number => {
    return (
        (missingSttCoverable.numberOfPersonOutsideHousehold ?? 0) -
        (missingSttCoverable.insuredPersonOutsideHouse?.length ?? 0)
    );
};

interface PetMissingInformationAndMatchingCoverable {
    missingInformation: PetMissingInformationDTO;
    coverable: CatDTO | DogDTO;
}

const missingCatProps: (cat: CatDTO) => PetMissingInformationDTO = (cat: CatDTO) => {
    return {
        hasNoChipNumber: cat.hasChipNumber,
        fixedId: cat.fixedId ?? '',
        chipNumber: cat.chipIdNumber
    };
};

const missingDogProps: (dog: DogDTO) => PetMissingInformationDTO = (dog: DogDTO) => {
    return {
        hasNoChipNumber: dog.hasChipNumber,
        fixedId: dog.fixedId ?? '',
        chipNumber: dog.chipIdNumber,
        dogUse: dog.dogUse ?? '',
        policeDogId: dog.policeDogID ?? ''
    };
};

const petToMissingInfoCoverable =
    (map: Record<string, PetMissingInformationDTO>) =>
    (pet: DogDTO | CatDTO): PetMissingInformationAndMatchingCoverable => ({
        missingInformation: _.get(map, pet.fixedId ?? ''),
        coverable: pet
    });

export type { PetMissingInformationAndMatchingCoverable };

const getPetMissingInformations = (
    coverables: AllCoverablesDTO
): PetMissingInformationDTO[] | undefined => {
    if (!coverables.cats && !coverables.dogs) {
        return undefined;
    }

    const petsMissingInfo: PetMissingInformationDTO[] = [];

    if (coverables.cats) {
        const missingCats = coverables.cats
            .filter((cat) => isMissingPetChipId(cat))
            .map(missingCatProps);
        petsMissingInfo.push(...missingCats);
    }

    if (coverables.dogs) {
        const missingDogs = coverables.dogs
            .filter((dog) => isMissingPetChipId(dog))
            .map(missingDogProps);
        petsMissingInfo.push(...missingDogs);
    }

    return petsMissingInfo;
};

const getSttMissingInformation = (
    coverables: AllCoverablesDTO
): SingleTripMissingInformationDTO[] | undefined => {
    if (!coverables.singleTripTravel) {
        return undefined;
    }

    const sttCoverable = coverables.singleTripTravel[0];

    if (isMissingSingleTripData(sttCoverable)) {
        return initialSingleTripMissingDTO(sttCoverable);
    }

    return undefined;
};

const getStcMissingInformation = (
    coverables: AllCoverablesDTO
): SingleTripMissingInformationDTO[] | undefined => {
    if (!coverables.singleTripCancellation) {
        return undefined;
    }

    const stcCoverable = coverables.singleTripCancellation[0];

    if (isMissingSingleTripData(stcCoverable)) {
        return initialSingleTripMissingDTO(stcCoverable);
    }

    return undefined;
};

const getChildAccMissingInformation = (
    coverables: AllCoverablesDTO
): ChildAccidentMissingInformationDTO[] | undefined => {
    if (!coverables.childAccidents) {
        return undefined;
    }

    return mapChildAccidentCoverableToMissingInformationDTO(coverables.childAccidents);
};

const getAccOtherPersonMissingInformation = (
    coverables: AllCoverablesDTO
): AccidentYoungAndOtherPersonMissingInformationDTO[] | undefined => {
    if (!coverables.adultAccidents && !coverables.youngAccidents) {
        return undefined;
    }

    return mapAccidentOtherPersonCoverableToMissingInformationDTO(
        coverables.adultAccidents ?? [],
        coverables.youngAccidents ?? []
    );
};

const getNumberInHouseholdAndCohousingNumberGap = (content: PrivateContentDTO): number => {
    return (content.numberInHousehold ?? 0) - (content.insuredPersonCohousings?.length ?? 0);
};

const getContentCohousingMissingInformation = (
    coverables: AllCoverablesDTO
): ContentMissingInformationDTO[] | undefined => {
    if (!coverables.privateContent) {
        return undefined;
    }

    const prcCoverable = coverables.privateContent[0];

    if (isMissingPRCCohousing(prcCoverable)) {
        return createMissingInformationDTOByQuantity(
            getNumberInHouseholdAndCohousingNumberGap(prcCoverable)
        );
    }

    return undefined;
};

const getContentCoinsuredMissingInformation = (
    coverables: AllCoverablesDTO
): ContentMissingInformationDTO[] | undefined => {
    if (!coverables.privateContent) {
        return undefined;
    }

    const prcCoverable = coverables.privateContent[0];

    if (isMissingPRCCoinsuredPartner(prcCoverable)) {
        return createMissingInformationDTOByQuantity(1);
    }

    return undefined;
};

const getCarMissingInformation = (
    coverables: AllCoverablesDTO
): CarMissingInformationDTO[] | undefined => {
    if (!coverables.cars) {
        return undefined;
    }

    return mapCarCoverableToMissingInformationDTO(coverables.cars);
};

const filterRemovedCoverables = (coverables: AllCoverablesDTO): AllCoverablesDTO => {
    const filteredCoverablesList: AllCoverablesDTO = {};
    for (const [key, value] of Object.entries(coverables)) {
        const filteredList = value.filter((item: any) => item.status !== 'Removed');
        filteredCoverablesList[key as keyof AllCoverablesDTO] = filteredList;
    }
    return filteredCoverablesList;
};

const getMissingInformationDTO = (coverables: AllCoverablesDTO): MissingInformationDTO => {
    const filteredCoverablesList = filterRemovedCoverables(coverables);

    return {
        pet: getPetMissingInformations(filteredCoverablesList),
        singleTripTravel: getSttMissingInformation(filteredCoverablesList),
        singleTripCancellation: getStcMissingInformation(filteredCoverablesList),
        childAccident: getChildAccMissingInformation(filteredCoverablesList),
        contentCohousing: getContentCohousingMissingInformation(filteredCoverablesList),
        contentCoinsured: getContentCoinsuredMissingInformation(filteredCoverablesList),
        accidentOtherPerson: getAccOtherPersonMissingInformation(filteredCoverablesList),
        car: getCarMissingInformation(filteredCoverablesList)
    };
};

export {
    getCpr,
    initialSingleTripMissingDTO,
    mapChildAccidentCoverableToMissingInformationDTO,
    reformatAndGetCprInformation,
    reformatCprWithoutHyphen,
    createMissingInformationDTOByQuantity,
    missingCatProps,
    missingDogProps,
    petToMissingInfoCoverable,
    getMissingInformationDTO,
    filterRemovedCoverables
};
